import React, { useCallback, useRef, useState } from 'react';
import MathText from '../../../../../common/MathText';
import { ANSWER_PLACEHOLDER, buildAnswerPreviewText, hasMathNotation } from '../../../../../../utils/shortAnswerMatch';
import { KEYBOARD_TABS, QUICK_KEYS } from './mathKeyboardLayout';

// Chen mau vao [start, end). Phan dang boi den duoc dua vao o □ dau tien
// (chon "x+1" roi bam √ -> √(x+1)); o □ tiep theo duoc chon san de go de len.
const applyTemplate = (text, start, end, template) => {
    const selected = text.slice(start, end);
    const firstSlot = template.indexOf(ANSWER_PLACEHOLDER);
    const fillsSlot = Boolean(selected) && firstSlot !== -1;
    const inserted = fillsSlot
        ? template.slice(0, firstSlot) + selected + template.slice(firstSlot + 1)
        : template;
    const nextSlot = inserted.indexOf(ANSWER_PLACEHOLDER, fillsSlot ? firstSlot + selected.length : 0);
    const caret = start + inserted.length;

    return {
        value: text.slice(0, start) + inserted + text.slice(end),
        selectionStart: nextSlot === -1 ? caret : start + nextSlot,
        selectionEnd: nextSlot === -1 ? caret : start + nextSlot + 1,
    };
};

// O □ ke tiep tinh tu vi tri `from`, het thi quay lai o dau tien.
const findNextPlaceholder = (text, from) => {
    const next = text.indexOf(ANSWER_PLACEHOLDER, from);
    return next === -1 ? text.indexOf(ANSWER_PLACEHOLDER) : next;
};

// Giu focus o o nhap khi bam phim ao de khong mat vi tri con tro.
const keepInputFocus = (event) => event.preventDefault();

/**
 * O nhap cau tra loi ngan kem ban phim ky hieu toan va dong xem truoc cong thuc.
 * Nguoi dung van co the go tay: sqrt(2), cbrt(5), pi, x^2, 1/2.
 */
export default function ShortAnswerInput({ value, onChange, placeholder }) {
    const inputRef = useRef(null);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const [activeTabId, setActiveTabId] = useState(KEYBOARD_TABS[0].id);
    const text = value || '';
    const activeTab = KEYBOARD_TABS.find((tab) => tab.id === activeTabId) || KEYBOARD_TABS[0];

    // Doi React ghi gia tri moi vao input roi moi dat lai con tro / vung chon.
    const restoreSelection = useCallback((start, end = start) => {
        window.setTimeout(() => {
            const input = inputRef.current;
            if (!input) {
                return;
            }
            input.focus();
            input.setSelectionRange(start, end);
        }, 0);
    }, []);

    // Chua focus vao o nhap thi coi nhu con tro dang o cuoi.
    const getSelection = useCallback(() => {
        const input = inputRef.current;
        if (input && document.activeElement === input) {
            return { start: input.selectionStart, end: input.selectionEnd };
        }
        return { start: text.length, end: text.length };
    }, [text]);

    const updateText = useCallback((nextValue, selectionStart, selectionEnd = selectionStart) => {
        onChange(nextValue);
        restoreSelection(selectionStart, selectionEnd);
    }, [onChange, restoreSelection]);

    const insertKey = useCallback((keyDef) => {
        const { start, end } = getSelection();
        // So mu/chi so gan vao sau phan dang chon thay vi go de len no.
        const result = applyTemplate(text, keyDef.attach ? end : start, end, keyDef.insert);
        updateText(result.value, result.selectionStart, result.selectionEnd);
    }, [getSelection, text, updateText]);

    const selectNextPlaceholder = useCallback(() => {
        const { end } = getSelection();
        const next = findNextPlaceholder(text, end);
        if (next !== -1) {
            restoreSelection(next, next + 1);
        }
        return next !== -1;
    }, [getSelection, restoreSelection, text]);

    const moveCaret = useCallback((step) => {
        const { start, end } = getSelection();
        const anchor = step < 0 ? start : end;
        const caret = Math.min(text.length, Math.max(0, anchor + (start === end ? step : 0)));
        restoreSelection(caret);
    }, [getSelection, restoreSelection, text.length]);

    const deleteBackward = useCallback(() => {
        const { start, end } = getSelection();
        if (start !== end) {
            updateText(text.slice(0, start) + text.slice(end), start);
        } else if (start > 0) {
            updateText(text.slice(0, start - 1) + text.slice(start), start - 1);
        }
    }, [getSelection, text, updateText]);

    const toggleKeyboard = useCallback(() => {
        setIsKeyboardOpen((open) => !open);
        // Doi inputMode khi dang focus: blur roi focus lai de dien thoai an/hien
        // ban phim he thong cho dung.
        const input = inputRef.current;
        if (input && document.activeElement === input) {
            const { selectionStart, selectionEnd } = input;
            input.blur();
            restoreSelection(selectionStart, selectionEnd);
        }
    }, [restoreSelection]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Tab' && !event.shiftKey && text.includes(ANSWER_PLACEHOLDER)) {
            event.preventDefault();
            selectNextPlaceholder();
        }
    }, [selectNextPlaceholder, text]);

    const renderKey = (keyDef) => (
        <button
            key={keyDef.id}
            type="button"
            className={`quiz-vkb-key${keyDef.variant ? ` quiz-vkb-key--${keyDef.variant}` : ''}`}
            title={keyDef.title}
            aria-label={keyDef.title || keyDef.insert}
            onMouseDown={keepInputFocus}
            onClick={() => insertKey(keyDef)}
        >
            {keyDef.label}
        </button>
    );

    const showPreview = hasMathNotation(text);

    return (
        <div className="quiz-short-answer">
            <input
                ref={inputRef}
                type="text"
                className="form-control form-control-lg quiz-text-input"
                placeholder={placeholder || 'Nhập câu trả lời...'}
                value={text}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                // Dang dung ban phim ao thi khong bat ban phim he thong tren dien thoai.
                inputMode={isKeyboardOpen ? 'none' : 'text'}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
            />

            {showPreview && (
                <div className="quiz-math-preview mt-2">
                    <span className="text-muted me-2">Xem trước:</span>
                    <MathText text={buildAnswerPreviewText(text)} />
                </div>
            )}

            {!isKeyboardOpen ? (
                <div className="quiz-vkb-quick" role="toolbar" aria-label="Ký hiệu thường dùng">
                    {QUICK_KEYS.map(renderKey)}
                    <button
                        type="button"
                        className="quiz-vkb-key quiz-vkb-key--toggle"
                        onMouseDown={keepInputFocus}
                        onClick={toggleKeyboard}
                    >
                        <i className="fas fa-keyboard me-2"></i>
                        Bàn phím toán
                    </button>
                </div>
            ) : (
                <div className="quiz-vkb" role="group" aria-label="Bàn phím ký hiệu toán học">
                    <div className="quiz-vkb-header">
                        <div className="quiz-vkb-tabs" role="tablist">
                            {KEYBOARD_TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab.id === activeTab.id}
                                    className={`quiz-vkb-tab${tab.id === activeTab.id ? ' active' : ''}`}
                                    onMouseDown={keepInputFocus}
                                    onClick={() => setActiveTabId(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="quiz-vkb-close"
                            title="Ẩn bàn phím"
                            aria-label="Ẩn bàn phím"
                            onMouseDown={keepInputFocus}
                            onClick={toggleKeyboard}
                        >
                            <i className="fas fa-chevron-down"></i>
                        </button>
                    </div>

                    <div className="quiz-vkb-body" role="tabpanel" aria-label={activeTab.label}>
                        {activeTab.groups.map((group, groupIndex) => (
                            <div key={group.title || groupIndex} className="quiz-vkb-group">
                                {group.title && <div className="quiz-vkb-group-title">{group.title}</div>}
                                <div className={`quiz-vkb-grid${activeTab.layout === 'numpad' ? ' quiz-vkb-grid--numpad' : ''}`}>
                                    {group.keys.map(renderKey)}
                                </div>
                            </div>
                        ))}
                        {activeTab.hint && <div className="quiz-vkb-hint">{activeTab.hint}</div>}
                    </div>

                    <div className="quiz-vkb-actions">
                        <button type="button" className="quiz-vkb-key quiz-vkb-key--action" title="Sang trái" aria-label="Sang trái" onMouseDown={keepInputFocus} onClick={() => moveCaret(-1)}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <button type="button" className="quiz-vkb-key quiz-vkb-key--action" title="Sang phải" aria-label="Sang phải" onMouseDown={keepInputFocus} onClick={() => moveCaret(1)}>
                            <i className="fas fa-arrow-right"></i>
                        </button>
                        <button type="button" className="quiz-vkb-key quiz-vkb-key--primary" title="Sang ô trống tiếp theo (Tab)" aria-label="Ô tiếp" onMouseDown={keepInputFocus} onClick={selectNextPlaceholder}>
                            Ô tiếp <span className="quiz-vkb-slot ms-1"></span>
                        </button>
                        <button type="button" className="quiz-vkb-key quiz-vkb-key--action" title="Xóa lùi" aria-label="Xóa lùi" onMouseDown={keepInputFocus} onClick={deleteBackward}>
                            <i className="fas fa-backspace"></i>
                        </button>
                        <button type="button" className="quiz-vkb-key quiz-vkb-key--action" title="Xóa hết" aria-label="Xóa hết" onMouseDown={keepInputFocus} onClick={() => updateText('', 0)}>
                            Xóa
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
